import { useEffect, useState } from "react";
import FormErrors from "components/formParts/formErrors";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isEmail } from "utils/validation";
import PasswordInput from "components/inputs/passwordInput";
import EmailInput from "components/inputs/emailInput";
import Button from "components/inputs/button"
import ButtonGroup from "components/buttons/buttonGroup"
import ProcessingButton from "components/inputs/processingButton"
import { ReactComponent as LoginIcon } from "assets/icons/login.svg";
import useLayout from "hooks/useLayout";
import useAuth from "hooks/useAuth";
import Form from "components/forms/form";
import Section from "components/layout/section";
import { InputMaxLength } from "types";
import { decodeFirebaseError } from "utils/firebaseHelpers";

const LoginPasswordForm = () => {

    const { t } = useTranslation()

    // Form data.
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isValid, setIsValid] = useState(false);

    // Form status.
    const [errors, setErrors] = useState<string[]>([]);

    // Auth hooks.
    const { login, isLoggingIn, loginError } = useAuth();

    // Layout hooks.
    const { addToast } = useLayout();

    // Convert error codes.
    const getErrorMessage = (errorType: string) => {
        switch (errorType) {
            case 'auth/email-already-in-use':
                return `Email already in use`
            case 'auth/invalid-email':
                return `Invalid email`
            case 'auth/weak-password':
                return `Weak password`
            default:
                return `Failed login.`
        }
    };

    // Handle submit.
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isValid) return
        setErrors([])
        
        try {
            console.log(1)
            await login({ email, password });
            console.log(2)
            addToast({ text: t('toastLoginSuccess'), colorScheme: 'green' })
        } catch (error: any) {
            console.log(3)
            const errorMessage = getErrorMessage(error?.code || error?.message || 'Unknown error')
            setErrors([errorMessage])
            addToast({ text: t('toastLoginError'), colorScheme: 'red' })
        }
    };

    // Handle email change.
    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value)
    }

    // Handle password change.
    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value)
    }

    // Validate input.
    useEffect(() => {
        const unsubscribe = () => {
            if (isEmail(email) && password.length > 0) {
                setIsValid(true)
            } else {
                setIsValid(false)
            }
        }
        unsubscribe();
        return unsubscribe;
    }, [email, password]);

    // Return the form.
    return (
        <Form
            onSubmit={handleSubmit}
            className={`group`}
            isLoading={isLoggingIn}
        >
            <Section>
                <EmailInput label={t('email')} value={email} onChange={handleEmailChange} required maxLength={InputMaxLength.medium} />
                <PasswordInput label={t('password')} value={password} onChange={handlePasswordChange} required />
                <Link to={`/lost-password`} className="link">{t('lostPassword')}?</Link>
            </Section>
            <Section>
                <ButtonGroup className="input-holder">
                    {isLoggingIn ? (
                        <ProcessingButton design="primary" />
                    ) : (
                        <Button wideOnMobile type="submit" design="primary" disabled={!isValid}><LoginIcon /><span>{t('login')}</span></Button>
                    )}
                </ButtonGroup>
                {errors.length > 0 && <FormErrors errors={errors} />}
            </Section>
        </Form>
    );
};

export default LoginPasswordForm;
